import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, Phone, MapPin, Facebook, Instagram, Send } from 'lucide-react';
import { useLanguage } from './LanguageContext';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState(''); // Honeypot
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const { language, t, getLocalizedPath } = useLanguage();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          "Formular": "Nyhedsbrev tilmelding",
          "E-mail": email,
          "Besked": `En bruger har tilmeldt sig nyhedsbrevet via websidens footer (${language === 'da' ? 'Dansk side' : 'Engelsk side'}).`,
          "Kilde": "Footer nyhedsbrev",
          website_url: websiteUrl, // Honeypot
        }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
        setWebsiteUrl('');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
    }
  };

  return (
    <footer className="bg-stone-900 text-white relative overflow-hidden">
      {/* Decorative background element */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#99A18C]/50 via-[#99A18C] to-[#99A18C]/50"></div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to={getLocalizedPath('home', language)} className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-[#99A18C] rounded-xl flex items-center justify-center">
                <Heart className="text-white w-6 h-6 fill-current" />
              </div>
              <span className="text-2xl font-serif font-bold italic">Alora Rejser</span>
            </Link>
            <p className="text-stone-400 leading-relaxed">
              {t('footer_tagline')}
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61572030426651" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#99A18C] transition-colors" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#99A18C] transition-colors" aria-label="Instagram">
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-serif font-bold mb-6 italic">{t('footer_info_header')}</h3>
            <ul className="space-y-4 text-stone-400">
              <li><Link to={getLocalizedPath('destinations', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_destinations')}</Link></li>
              <li><Link to={getLocalizedPath('hotels', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_hotels')}</Link></li>
              <li><Link to={getLocalizedPath('featured-trips', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_featured_trips')}</Link></li>
              <li><Link to={getLocalizedPath('prices', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_prices')}</Link></li>
              <li><Link to={getLocalizedPath('about', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_about')}</Link></li>
              <li><Link to={getLocalizedPath('quote', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_quote')}</Link></li>
              <li><Link to={getLocalizedPath('terms', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_terms')}</Link></li>
              <li><Link to={getLocalizedPath('practical', language)} className="hover:text-[#99A18C] transition-colors">{t('practical_info_header') || t('footer_info_header')}</Link></li>
              <li><Link to={getLocalizedPath('gallery', language)} className="hover:text-[#99A18C] transition-colors">{t('gallery_header') || t('nav_traveltips')}</Link></li>
            </ul>
          </div>

          {/* Legal & Privacy */}
          <div>
            <h3 className="text-lg font-serif font-bold mb-6 italic">{t('footer_privacy_header')}</h3>
            <ul className="space-y-4 text-stone-400">
              <li><Link to={getLocalizedPath('gdpr', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_gdpr') || 'GDPR'}</Link></li>
              <li><Link to={getLocalizedPath('consent', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_consent') || 'Consent'}</Link></li>
              <li><Link to={getLocalizedPath('contact', language)} className="hover:text-[#99A18C] transition-colors">{t('nav_contact')}</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-serif font-bold mb-6 italic">{t('footer_newsletter_header')}</h3>
            <p className="text-sm text-stone-400 mb-6">
              {t('footer_newsletter_desc')}
            </p>
            {status === 'success' ? (
              <div className="bg-emerald-950/40 border border-emerald-500/20 text-emerald-200 p-5 rounded-2xl text-sm leading-relaxed animate-in fade-in duration-300">
                <p className="font-semibold text-white mb-1">{t('footer_newsletter_success')}</p>
                <button 
                  onClick={() => setStatus('idle')} 
                  className="mt-3 text-xs text-[#99A18C] hover:underline font-medium cursor-pointer"
                >
                  {language === 'da' ? 'Tilmeld en anden e-mail' : 'Subscribe another email'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="relative">
                {/* Honeypot field - HIDDEN FROM HUMANS */}
                <div className="sr-only absolute -top-[9999px] left-[-9999px]">
                  <label htmlFor="website_url_newsletter">
                    {language === 'da' ? 'Hvis du ser dette, så lad det stå tomt' : 'If you see this, leave it blank'}
                  </label>
                  <input
                    type="text"
                    id="website_url_newsletter"
                    name="website_url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer_newsletter_placeholder')} 
                  disabled={status === 'loading'}
                  className="w-full bg-white/5 border border-white/10 py-4 px-6 rounded-2xl outline-none focus:border-[#99A18C] transition-colors pr-12 text-sm disabled:opacity-50"
                />
                <button 
                  type="submit"
                  disabled={status === 'loading'}
                  className="absolute right-2 top-2 bottom-2 bg-[#99A18C] hover:bg-[#8a917e] px-3 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center cursor-pointer"
                  aria-label={t('footer_newsletter_button')}
                >
                  {status === 'loading' ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Send size={18} />
                  )}
                </button>
                {status === 'error' && (
                  <p className="text-xs text-red-400 mt-2">{t('footer_newsletter_error')}</p>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Rejsegarantifonden Section */}
        <div className="pt-8 pb-4 border-t border-white/5 flex flex-col items-center justify-center text-center">
          <div className="flex flex-col sm:flex-row items-center gap-6 bg-[#99A18C] text-white px-8 py-4 rounded-2xl border border-[#99A18C] max-w-md mx-auto mb-3 shadow-lg">
            <img 
              src="https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/c2f066c1-bc76-4f8b-b35f-4eed17c9957a.png" 
              alt="Rejsegarantifonden" 
              className="h-14 w-auto object-contain rounded-lg"
            />
            <div className="text-center sm:text-left flex flex-col gap-1">
              <p className="text-xs text-white/80 font-semibold tracking-wider uppercase">Rejsegarantifonden</p>
              <p className="text-lg text-white font-medium">Medlemsnr. 3839</p>
              <p className="text-xs text-white/70">{language === 'da' ? 'Din sikkerhed for en tryg rejse' : 'Your safety for a secure journey'}</p>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 text-center text-sm text-stone-500 flex flex-col md:flex-row justify-between items-center gap-4">
          <p>© {new Date().getFullYear()} Alora Rejser. {t('footer_cvr')}: 46391772</p>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2 font-medium"><Phone size={14} className="text-[#99A18C]" /> 40 50 07 80</span>
            <span className="flex items-center gap-2 font-medium"><Mail size={14} className="text-[#99A18C]" /> info@alorarejser.dk</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
